import React, { useEffect, useMemo, useState } from "react";
import "./Games.css";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import games from "../data/gamesData";
import { resolveLanding } from "../utils/appSeo";
import Footer from "../components/Footer";
import PageWrapper from "../components/PageWrapper";
import { setPageMeta } from "../utils/pageMeta";

const FILTERS = [
  {
    id: "all",
    labelKey: "filter_all",
    categories: null,
  },
  {
    id: "games",
    labelKey: "filter_games",
    categories: ["cat_game", "cat_party", "cat_trivia", "cat_casino"],
  },
  {
    id: "utilities",
    labelKey: "filter_utilities",
    categories: ["cat_utility", "cat_education"],
  },
  {
    id: "health",
    labelKey: "filter_health",
    categories: ["cat_health"],
  },
];

export default function Games() {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    setPageMeta({
      title: t("our_apps"),
      description: t("apps_page_subtitle"),
      url: "https://mindblows.com/apps",
      robots: "index, follow",
    });
  }, [t]);

  const filteredApps = useMemo(() => {
    const filter = FILTERS.find((item) => item.id === activeFilter);
    const normalizedQuery = query.trim().toLowerCase();

    return games.filter((app) => {
      const matchesFilter =
        !filter?.categories || filter.categories.includes(app.categoryKey);

      if (!matchesFilter) return false;
      if (!normalizedQuery) return true;

      const landing = resolveLanding(app);
      const haystack = [
        app.shortTitle,
        app.title,
        app.subtitle,
        landing.seoTitle,
        t(app.categoryKey),
        ...(landing.keywords || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedQuery);
    });
  }, [activeFilter, query, t]);

  return (
    <PageWrapper>
      <div className="apps-page">
        <header className="apps-header">
          <div>
            <h1>{t("our_apps")}</h1>
            <p className="apps-subtitle">{t("apps_page_subtitle")}</p>
          </div>
          <span className="apps-count">
            {t("apps_showing", {
              count: filteredApps.length,
              total: games.length,
            })}
          </span>
        </header>

        <div className="apps-toolbar">
          <input
            type="search"
            className="apps-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("apps_search_placeholder")}
            aria-label={t("apps_search_placeholder")}
          />

          <div
            className="apps-filters"
            role="group"
            aria-label={t("apps_filters_label")}
          >
            {FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                aria-pressed={activeFilter === filter.id}
                className={`apps-filter ${
                  activeFilter === filter.id ? "active" : ""
                }`}
                onClick={() => setActiveFilter(filter.id)}
              >
                {t(filter.labelKey)}
              </button>
            ))}
          </div>
        </div>

        {filteredApps.length === 0 ? (
          <div className="apps-empty">
            <p>{t("apps_empty")}</p>
            <button
              type="button"
              className="apps-empty-reset"
              onClick={() => {
                setQuery("");
                setActiveFilter("all");
              }}
            >
              {t("apps_reset_filters")}
            </button>
          </div>
        ) : (
          <div className="apps-grid">
            {filteredApps.map((app) => (
              <article key={app.id} className="apps-card">
                <Link to={`/apps/${app.id}`} className="apps-card-main">
                  <img
                    src={app.icon}
                    alt={app.shortTitle}
                    className="apps-card-icon"
                    loading="lazy"
                    width={72}
                    height={72}
                  />
                  <div className="apps-card-body">
                    <div className="apps-card-top">
                      <h2>{app.title || app.shortTitle}</h2>
                      <span className="apps-card-category">
                        {t(app.categoryKey)}
                      </span>
                    </div>
                    <p className="apps-card-subtitle">
                      {app.subtitle || app.title}
                    </p>
                    {app.downloads ? (
                      <span className="apps-card-meta">{app.downloads}</span>
                    ) : null}
                  </div>
                </Link>

                <div className="apps-card-stores">
                  {app.appStoreUrl && (
                    <a
                      href={app.appStoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="apps-store-link"
                    >
                      App Store
                    </a>
                  )}
                  {app.playStoreUrl && (
                    <a
                      href={app.playStoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="apps-store-link"
                    >
                      Google Play
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </PageWrapper>
  );
}
